import classNames from "classnames";
import { useState } from "react";
import { Button } from "../../../components/Button/Button.tsx";
import { ClickableIcon } from "../../../components/Icon/ClickableIcon.tsx";
import { Icon } from "../../../components/Icon/Icon.tsx";
import { Popover } from "../../../components/Popover/Popover.tsx";
import { Spinner } from "../../../components/Spinner/Spinner.tsx";
import { useAppMutation } from "../../../http/useAppMutation.ts";
import { useWorkspace } from "../../hooks/useWorkspace.ts";
import { useWorkspaceDetails } from "../../hooks/useWorkspaceDetails.ts";
import type { Member } from "../../types.ts";
import { AddUsersDialog } from "../HomeSidebar/AddUsersDialog.tsx";
import { SettingsContentLayout } from "./components/SettingsContentLayout.tsx";

export const WorkspaceMembersSettings = () => {
  const { details, isLoading } = useWorkspaceDetails({});
  const { workspace } = useWorkspace();
  const [isAddUserDialogOpen, setIsAddUsersDialogOpen] = useState(false);

  const removeUser = useAppMutation({
    path: "workspaces/remove-user",
    invalidate: [
      `workspaces/${workspace.uuid}`,
      `workspaces/${workspace.uuid}/details`,
    ],
  }).mutation;

  const removeUserFromWorkspace = (userUuid: string) => {
    removeUser.mutate({ workspace_uuid: workspace.uuid, user_uuid: userUuid });
  };

  return (
    <>
      <SettingsContentLayout
        header={
          <div className="w-full">
            <div className="flex-row-center overflow-visible">
              <div className="flex-col flex-fill">
                <div className="label-lg-semibold text-primary">Members</div>
                {isLoading ? (
                  <Spinner size="xs" />
                ) : (
                  details?.num_seats && (
                    <div className="label-md-semibold text-secondary">
                      {details.num_members}/{details.num_seats} used seats
                    </div>
                  )
                )}
              </div>
              <div className="flex-row gap-200">
                <Button
                  size="md"
                  variant="primary"
                  onClick={() => {
                    setIsAddUsersDialogOpen(true);
                  }}
                >
                  Invite members
                </Button>
              </div>
            </div>
          </div>
        }
        items={[
          <div
            key="users"
            className="flex-col-center divide-y-025 divide-solid divide-primary-rest"
          >
            <div className="w-full h-800 flex-row items-center">
              <div className="w-[368px] flex-row pr-200 label-sm-semibold text-disabled">
                User
              </div>
              <div className="w-[104px] flex-row-reverse px-200 label-sm-semibold text-disabled">
                Role
              </div>
              <div className="w-800 h-full" />
            </div>
            {workspace.users.map((item) => (
              <UserItem
                key={item.uuid}
                user={item}
                onRemoveClick={removeUserFromWorkspace}
              />
            ))}
          </div>,
        ]}
      />
      <AddUsersDialog
        isOpen={isAddUserDialogOpen}
        onOpenChange={(open) => setIsAddUsersDialogOpen(open)}
      />
    </>
  );
};

const UserItem = ({
  user,
  onRemoveClick,
}: {
  user: Member;
  onRemoveClick: (userUuid: string) => void;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  return (
    <div key={user.uuid} className="w-full h-1200 flex-row">
      <div className="flex-row items-center">
        <div className="w-[368px] flex-row-center pr-200 gap-200">
          <Icon
            name="User"
            size="sm"
            className={classNames(
              user.is_owner ? "stroke-primary-disabled" : "",
            )}
          />
          <div className="flex-fill flex-col">
            <div className="label-md-semibold text-primary">
              {user.first_name} {user.last_name}
            </div>
            <div className="label-sm-semibold text-secondary">{user.email}</div>
          </div>
        </div>
        <div
          className={classNames(
            "w-[104px] flex-row-reverse px-200 label-sm-semibold",
            user.is_owner ? "text-disabled" : "text-primary",
          )}
        >
          {user.is_owner ? "Owner" : "Member"}
        </div>
      </div>
      <div className="flex-col-center w-800 h-full">
        <Popover
          isOpen={isPopoverOpen}
          onOpenChange={setIsPopoverOpen}
          align="start"
          side="bottom"
          content={
            <div className="flex-col p-200">
              <Button
                onClick={() => {
                  onRemoveClick(user.uuid);
                  setIsPopoverOpen(false);
                }}
                iconName="Trash"
                size="md"
                variant="tertiary"
                className="label-md-default text-primary"
              >
                Remove from workspace
              </Button>
            </div>
          }
        >
          <ClickableIcon
            name="Ellipsis"
            variant="tertiary"
            size="sm"
            disabled={user.is_owner}
            onClick={() => setIsPopoverOpen(!isPopoverOpen)}
          />
        </Popover>
      </div>
    </div>
  );
};
